import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Hero } from './components/Hero';
import { useMetaPixel } from './utils/metaPixel';
// Lazy load all below-fold components to reduce initial JS bundle
// This code-splits framer-motion out of the critical path
const StatsAndTestimonials = lazy(() => import('./components/StatsAndTestimonials').then(m => ({
  default: m.StatsAndTestimonials
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const Modules = lazy(() => import('./components/Modules').then(m => ({
  default: m.Modules
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const CostComparison = lazy(() => import('./components/CostComparison').then(m => ({
  default: m.CostComparison
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseToast = lazy(() => import('./components/PurchaseToast').then(m => ({
  default: m.PurchaseToast
})));
export function App() {
  useMetaPixel();
  useEffect(() => {
    // Set language
    document.documentElement.lang = 'es';
    // Set page title
    document.title = 'Adulto de Rescate™ — Kit Anti-Crisis para Padres de Adolescentes';
    // Preconnect to critical origins for faster resource loading
    const preconnects = [{
      href: 'https://cdn.magicpatterns.com',
      crossorigin: false
    }, {
      href: 'https://fonts.googleapis.com',
      crossorigin: false
    }, {
      href: 'https://fonts.gstatic.com',
      crossorigin: true
    }];
    const preconnectElements: HTMLLinkElement[] = [];
    preconnects.forEach(({
      href,
      crossorigin
    }) => {
      if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        if (crossorigin) link.crossOrigin = '';
        document.head.insertBefore(link, document.head.firstChild);
        preconnectElements.push(link);
      }
    });
    // Load Google Fonts asynchronously (non-render-blocking)
    const fontsUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap';
    const existingFontLink = document.querySelector(`link[href="${fontsUrl}"]`);
    let fontLink: HTMLLinkElement | null = null;
    if (!existingFontLink) {
      fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = fontsUrl;
      fontLink.media = 'print';
      fontLink.onload = function () {
        ;
        (this as HTMLLinkElement).media = 'all';
      };
      document.head.appendChild(fontLink);
      // Fallback for browsers with JS disabled or onload issues
      const noscript = document.createElement('noscript');
      const fallbackLink = document.createElement('link');
      fallbackLink.rel = 'stylesheet';
      fallbackLink.href = fontsUrl;
      noscript.appendChild(fallbackLink);
      document.head.appendChild(noscript);
    }
    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', 'Kit Adulto de Rescate: herramientas tácticas para padres de adolescentes. 6 módulos + 4 bonos para calmar ansiedad, frenar explosiones y reconectar con tu hijo. Acceso inmediato.');
    if (!document.querySelector('meta[name="description"]')) document.head.appendChild(metaDesc);
    // Viewport (ensure it exists)
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      document.head.appendChild(viewport);
    }
    // Open Graph tags
    const ogTags = [{
      property: 'og:title',
      content: 'Adulto de Rescate™ — Kit Anti-Crisis para Padres de Adolescentes'
    }, {
      property: 'og:description',
      content: 'Herramientas tácticas para calmar ansiedad, frenar explosiones y reconectar con tu hijo adolescente.'
    }, {
      property: 'og:type',
      content: 'product'
    }, {
      property: 'og:image',
      content: 'https://cdn.magicpatterns.com/uploads/45gAX5xrH7vkndVPg7MKsi/Diseno_sin_titulo_(2).png'
    }, {
      property: 'og:locale',
      content: 'es_AR'
    }];
    ogTags.forEach(({
      property,
      content
    }) => {
      const existing = document.querySelector(`meta[property="${property}"]`);
      if (!existing) {
        const meta = document.createElement('meta');
        meta.setAttribute('property', property);
        meta.setAttribute('content', content);
        document.head.appendChild(meta);
      }
    });
    // JSON-LD structured data
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Kit Adulto de Rescate™',
      description: 'Kit Anti-Crisis para padres de adolescentes. 6 módulos + 4 bonos con herramientas tácticas para calmar ansiedad, frenar explosiones y reconectar con tu hijo.',
      image: 'https://cdn.magicpatterns.com/uploads/45gAX5xrH7vkndVPg7MKsi/Diseno_sin_titulo_(2).png',
      brand: {
        '@type': 'Brand',
        name: 'Adulto de Rescate'
      },
      offers: [{
        '@type': 'Offer',
        name: 'Kit Adulto de Rescate',
        price: '19990',
        priceCurrency: 'ARS',
        availability: 'https://schema.org/InStock',
        url: 'https://fegion.shop/cart/46824945516683:1'
      }, {
        '@type': 'Offer',
        name: 'Kit Adulto de Rescate + Deck Táctico',
        price: '27990',
        priceCurrency: 'ARS',
        availability: 'https://schema.org/InStock',
        url: 'https://fegion.shop/cart/46824994341003:1'
      }],
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '127'
      }
    });
    document.head.appendChild(jsonLd);
    return () => {
      jsonLd.remove();
      preconnectElements.forEach(el => el.remove());
      if (fontLink) fontLink.remove();
    };
  }, []);
  return <div className="min-h-screen bg-background text-gray-300 font-body selection:bg-gold selection:text-background" data-id="element-0">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-gold focus:text-background focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold" data-id="element-1">
        Saltar al contenido principal
      </a>
      <main id="main-content" data-id="element-2">
        <Hero data-id="element-3" />
        <Suspense fallback={null} data-id="element-4">
          <StatsAndTestimonials data-id="element-5" />
          <PainPoints data-id="element-6" />
          <Modules data-id="element-7" />
          <Bonuses data-id="element-8" />
          <Pricing data-id="element-9" />
          <CostComparison data-id="element-10" />
          <FinalCTA data-id="element-11" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-12">
        <Footer data-id="element-13" />
        <PurchaseToast data-id="element-14" />
      </Suspense>
    </div>;
}